import {
  AlertBar,
  Icons,
  isPopulatedArray,
  Spinner,
} from "@win-reality/react-ui";
import { useEffect } from "react";

import { useTimeSlotAvailability } from "./TimeSlotAvailability.queries";
import { getEventLabel, getEventTimeRange } from "./TimeSlotAvailability.utils";

type TimeSlotAvailabilityProps = {
  fieldId?: string;
  startTime?: string;
  endTime?: string;
  startDate?: Date;
  onAvailabilityChange?: (isAvailable: boolean) => void;
  excludeScheduleId?: string;
};

function TimeSlotAvailability({
  fieldId,
  startTime,
  endTime,
  startDate,
  onAvailabilityChange,
  excludeScheduleId,
}: TimeSlotAvailabilityProps) {
  const areDatesConfigured = startTime && endTime && startDate && fieldId;

  const { events, isPending, isError } = useTimeSlotAvailability({
    fieldId,
    startTime,
    endTime,
    startDate,
    excludeScheduleId,
  });

  useEffect(() => {
    if (!areDatesConfigured) {
      onAvailabilityChange?.(true);
      return;
    }
    if (isPending || isError) {
      onAvailabilityChange?.(false);
      return;
    }
    onAvailabilityChange?.(events.length === 0);
  }, [areDatesConfigured, isPending, isError, events, onAvailabilityChange]);

  if (isPending && areDatesConfigured) {
    return (
      <AlertBar variant="info">
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          Checking availability...
        </div>
      </AlertBar>
    );
  }

  if (!isPopulatedArray(events) && areDatesConfigured && !isPending && !isError)
    return (
      <AlertBar variant="success">
        <div className="flex items-center gap-2">
          <Icons.ClockOutline className="size-4" />
          Time slot is available
        </div>
      </AlertBar>
    );

  return (
    <div className="flex flex-col gap-2">
      {events.map((event, idx) => (
        <AlertBar key={idx} variant="error">
          <span className="font-bold">{getEventLabel(event)}</span> (
          {getEventTimeRange(event)}) has already been scheduled for this field.
          Please change the date or time for this new event.
        </AlertBar>
      ))}
    </div>
  );
}

export { TimeSlotAvailability };
